/**
 * The items table, kept live without refetching 100 rows every 2 seconds.
 *
 * While the experiment runs, each poll asks only for rows changed since the
 * last answer (`updated_after` = the previous `server_time`) and merges them into
 * the cached page by id. Everything stays inside TanStack Query: the merge is the
 * query function, so there is one cache and no copy of it anywhere else.
 *
 * It falls back to a full refetch whenever a merge could be wrong — a filter or a
 * sort other than position (a changed row may move in or out of view), rows the
 * page has not seen that could belong on it, or more changes than one delta page.
 */

import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, withQuery } from "@/lib/api";
import { invalidatePaths } from "@/lib/query";
import type { ItemPage, ItemQuery } from "../types";

const DELTA_PAGE = 500;

export function mergeDelta(prev: ItemPage, delta: ItemPage): ItemPage | null {
  if (delta.total > delta.items.length) return null;
  const known = new Set(prev.items.map((r) => r.id));
  const pageHasRoom = prev.items.length < prev.page_size;
  if (pageHasRoom && delta.items.some((r) => !known.has(r.id))) return null;
  const changed = new Map(delta.items.map((r) => [r.id, r]));
  return {
    ...prev,
    items: prev.items.map((r) => changed.get(r.id) ?? r),
    server_time: delta.server_time,
  };
}

export function useExperimentItems(id: string, query: ItemQuery, live: boolean) {
  const queryClient = useQueryClient();
  const path = `/experiments/${id}/items`;
  const params = { ...query, page_size: 100 };
  const key = [path, params];
  const fetchItems = (q: Parameters<typeof withQuery>[1]) => api.get<ItemPage>(withQuery(path, q));
  const mergeable = !query.status && (!query.sort || query.sort === "position");

  const result = useQuery({
    queryKey: key,
    queryFn: async () => {
      const full = () => fetchItems(params);
      const prev = queryClient.getQueryData<ItemPage>(key);
      if (!live || !mergeable || !prev) return full();
      const delta = await fetchItems({
        updated_after: prev.server_time,
        page: 1,
        page_size: DELTA_PAGE,
      });
      return mergeDelta(prev, delta) ?? full();
    },
    refetchInterval: live ? 2000 : false,
  });

  // The last poll can land just before the final rows are written; once the run
  // settles, one full refetch makes the table agree with the finished summary.
  const wasLive = useRef(live);
  useEffect(() => {
    if (wasLive.current && !live) {
      void invalidatePaths(queryClient, [path]);
    }
    wasLive.current = live;
  }, [live, path, queryClient]);

  return result;
}
